import React, { type ComponentProps, useEffect, useRef, useState } from 'react';

import { convert } from '@revenuemarket/utils';
import Input, { type InputRef } from 'antd/lib/input';

export interface FormattedInputProps<T>
  extends Omit<ComponentProps<typeof Input>, 'value' | 'defaultValue' | 'onChange'> {
  value?: T;
  defaultValue?: T;
  onChange?: (value?: T) => void;
  formatter?: (value: T) => string;
  deformatter?: (value: string) => T;
}

export const FormattedInput = <T,>({
  value,
  defaultValue,
  onChange,
  formatter = (v) => String(v),
  deformatter = (v) => v as unknown as T,
  ...props
}: FormattedInputProps<T>) => {
  const inputRef = useRef<InputRef>(null);
  const prevCursor = useRef<number>(0);
  const [innerValue, setInnerValue] = useState(convert(value ?? defaultValue, formatter));

  const setValue = (value: string | undefined) => {
    setInnerValue(value);
    if (value != null) {
      onChange?.(deformatter(value));
    }
  };

  useEffect(() => {
    if (
      innerValue != null &&
      innerValue.length > 0 &&
      prevCursor.current !== inputRef.current?.input?.selectionEnd
    ) {
      inputRef.current?.setSelectionRange(prevCursor.current, prevCursor.current);
    }
  }, [innerValue]);

  useEffect(() => {
    if (defaultValue) {
      const formattedValue = formatter(deformatter(defaultValue as string));
      setInnerValue(formattedValue as string | Extract<T, undefined> | undefined);
    }
  }, [defaultValue]);

  return (
    <Input
      ref={inputRef}
      value={innerValue}
      onChange={(e) => {
        const targetValue = e.target.value;

        if (targetValue == null || targetValue === '') {
          setValue(undefined);
          onChange?.(undefined);
          return;
        }

        const formattedValue = formatter(deformatter(targetValue));

        prevCursor.current =
          (e.currentTarget.selectionEnd ?? 0) + (formattedValue.length - targetValue.length);

        setValue(formattedValue);
      }}
      {...props}
    />
  );
};
